import React from "react";
import type { PulseTracking } from "@m10s/search-frontend-clients/quest";
import { SemanticAd } from "src/types/SemanticAd";
import { ErrorState, getSearchStore, SearchStoreValue } from "../stores/searchStore";

export function useSearchResults() {
	const [results, setResults] = React.useState<SemanticAd[]>([]);
	const [currentQuery, setCurrentQuery] = React.useState<string>("");
	const [resultsQuery, setResultsQuery] = React.useState<string>("");
	const [showZeroResults, setShowZeroResults] = React.useState<boolean>(false);
	const [errorState, setErrorState] = React.useState<ErrorState | undefined>(undefined);
	const [hasTrackedResults, setHasTrackedResults] = React.useState<boolean>(false);
	const [tracking, setTracking] = React.useState<PulseTracking | undefined>(undefined);
	const storeRef = React.useRef(getSearchStore());

	React.useEffect(() => {
		const store = storeRef.current;

		const unsubscribe = store.subscribe((value: SearchStoreValue) => {
			const newQuery = value.q || "";
			const newResults = value.docs ?? [];
			const newError = value.error;
			const newResultsQuery = value.resultsQuery || "";
			const newHasTrackedResults = Boolean(value.hasTrackedResults);

			setResults(newResults);
			setCurrentQuery(newQuery);
			setErrorState(newError);
			setResultsQuery(newResultsQuery);
			setHasTrackedResults(newHasTrackedResults);
			setTracking(value.tracking);

			// Reset showZeroResults when query changes or results arrive
			setShowZeroResults(false);

			// If we have an error, don't show zero results (show error instead)
			if (newError?.hasError) {
				return;
			}

			// If we have a query but no results, start timer to show ZeroResults
			if (newQuery && newResults.length === 0) {
				setTimeout(() => {
					// Double-check we still have the same query and no results
					const current = store.get?.();
					if (current?.q === newQuery && (!current.docs || current.docs.length === 0) && !current.error?.hasError) {
						setShowZeroResults(true);
					}
				}, 1000);
			}
		});

		try {
			const current = store.get?.();
			if (current) {
				const initialQuery = current.q || "";
				const initialResults = current.docs || [];
				const initialError = current.error;
				const initialResultsQuery = current.resultsQuery || "";
				const initialHasTrackedResults = Boolean(current.hasTrackedResults);

				setResults(initialResults);
				setCurrentQuery(initialQuery);
				setErrorState(initialError);
				setResultsQuery(initialResultsQuery);
				setHasTrackedResults(initialHasTrackedResults);
				setTracking(current.tracking);

				// If we have a query but no results on initial load (and no error)
				if (initialQuery && initialResults.length === 0 && !initialError?.hasError) {
					setTimeout(() => {
						const updated = store.get?.();
						if (
							updated?.q === initialQuery &&
							(!updated.docs || updated.docs.length === 0) &&
							!updated.error?.hasError
						) {
							setShowZeroResults(true);
						}
					}, 1000);
				}
			}
		} catch (e) {
			console.warn("Search store get not available", e);
		}

		return () => {
			if (typeof unsubscribe === "function") unsubscribe();
		};
	}, []);

	return React.useMemo(
		() => ({
			results,
			currentQuery,
			resultsQuery,
			hasTrackedResults,
			showZeroResults,
			errorState,
			tracking,
		}),
		[results, currentQuery, resultsQuery, hasTrackedResults, showZeroResults, errorState, tracking],
	);
}
